import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { ReportAPI } from "@repo/api/report";
import { ReportType } from "@repo/api/report.type";
import type { StallInfo } from "@repo/api/report.type";
import { UserAPI } from "@repo/api/user";
import { User } from "@/types/user";
import { Rsp } from "@repo/types";

interface ReportTargetCardProps {
    /** 被举报对象的ID */
    targetId: string;
    /** 举报类型：user/stall 或 1/2 */
    type: ReportType | number | string;
    /** 加载失败时的回调 */
    onLoadError?: (error: string) => void;
}

/**
 * 被举报对象信息卡片组件
 * 用于显示被举报的用户或橱窗信息
 */
export function ReportTargetCard({ targetId, type, onLoadError }: ReportTargetCardProps) {
    const [targetInfo, setTargetInfo] = useState<{
        user?: User.Profile;
        stall?: StallInfo;
    }>({});
    const [isLoading, setIsLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);

    // 判断类型
    const typeValue = String(type);
    const isUserType = typeValue === ReportType.USER || typeValue === "1";
    const isStallType = typeValue === ReportType.STALL || typeValue === "2";

    useEffect(() => {
        if (!targetId) {
            setIsLoading(false);
            setLoadFailed(true);
            return;
        }

        setIsLoading(true);
        setLoadFailed(false);

        if (isUserType) {
            UserAPI.getUserProfile(targetId)
                .then((response) => {
                    setTargetInfo({ user: response.data });
                })
                .catch((error: Rsp<null>) => {
                    console.error("加载用户信息失败:", error);
                    setLoadFailed(true);
                    onLoadError?.(error.msg);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else if (isStallType) {
            ReportAPI.getStallInfo(targetId)
                .then((response) => {
                    setTargetInfo({ stall: response.data });
                })
                .catch((error: Rsp<null>) => {
                    console.error("加载橱窗信息失败:", error);
                    setLoadFailed(true);
                    onLoadError?.(error.msg);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            setIsLoading(false);
            setLoadFailed(true);
        }
    }, [targetId, isUserType, isStallType]);

    // 加载中状态
    if (isLoading) {
        return (
            <div className="bg-white flex items-center justify-center p-4 rounded-lg w-full">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">加载中...</span>
            </div>
        );
    }

    // 用户信息
    if (isUserType && targetInfo.user) {
        const user = targetInfo.user;
        return (
            <div className="bg-white flex items-center gap-3 p-4 rounded-lg w-full">
                <img
                    src={user.avatar_url}
                    alt={user.nickname}
                    className="w-12 h-12 rounded-full object-cover"
                />
                <div className="flex-1">
                    <div className="font-medium text-black">{user.nickname}</div>
                    <div className="text-sm text-gray-500">UID: {user.uid}</div>
                </div>
            </div>
        );
    }

    // 橱窗信息
    if (isStallType && targetInfo.stall) {
        const stall = targetInfo.stall;
        const firstImage = stall.images && stall.images.length > 0 ? stall.images[0].url : "";
        return (
            <div className="bg-white flex items-center gap-3 p-4 rounded-lg w-full">
                {firstImage && (
                    <img
                        src={firstImage}
                        alt={stall.title}
                        className="w-16 h-16 rounded object-cover"
                    />
                )}
                <div className="flex-1">
                    <div className="font-medium text-black line-clamp-2">{stall.title}</div>
                    <div className="text-sm text-red-500">
                        ¥{(stall.min_price / 100).toFixed(2)} 起
                    </div>
                </div>
            </div>
        );
    }

    // 加载失败或无数据
    return (
        <div className="bg-white flex items-center justify-center p-4 rounded-lg w-full">
            <span className="text-sm text-gray-500">
                {loadFailed ? "加载失败" : `目标ID: ${targetId}`}
            </span>
        </div>
    );
}
